import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Car, Smartphone, Bus, Ship, Zap, RotateCcw, Check, Info, ExternalLink, Anchor } from "lucide-react";
import { QUIZ_QUESTIONS, TRANSPORT, SHORELINK, track } from "@/lib/transport";
import { Reveal, MaskedLines } from "@/components/motion-primitives";
import { usePageMeta } from "@/components/shared";
import { IMAGES } from "@/lib/site";

const ICONS = { car: Car, smartphone: Smartphone, bus: Bus, ship: Ship, zap: Zap };
const STORAGE_KEY = "bch_transport_v1";

const PARTY_TEXT = { 0: "travelling solo", 1: "travelling as a couple", 2: "travelling as a small group", 3: "travelling with a larger group" };

function buildExplanation(winnerKey, answers) {
  const party = PARTY_TEXT[answers.party] || "travelling";
  const driving = answers.driving === 0;
  const flexible = answers.schedule === 1;
  const bits = [];
  if (winnerKey === "microcar") {
    bits.push(`Because you're ${party}, want maximum flexibility${driving ? " and are comfortable driving on the left" : ""}, an electric microcar could be the easiest way to explore Bermuda's beaches and attractions entirely at your own pace.`);
  } else if (winnerKey === "taxi") {
    bits.push(`Because you're ${party} and value simple, door-to-door travel${answers.timing === 1 ? " — including evenings" : ""}, taxis remove every bit of friction: no schedules, no parking, no left-side driving.`);
  } else if (winnerKey === "bus") {
    bits.push(`Because keeping costs down matters and you're happy following a schedule, Bermuda's pink buses will carry you between beaches and attractions for a fraction of any other option.`);
  } else if (winnerKey === "ferry") {
    bits.push(`Because you want the journey itself to be part of the vacation, the ferry turns harbour and West End travel into one of your Bermuda highlights.`);
  } else {
    bits.push(`Because you're ${party} and want flexible, app-based, cashless transport${answers.timing === 1 ? " — especially in the evenings" : ""}, Bermuda's regulated ride sharing fits your trip style neatly.`);
  }
  if (flexible) bits.push("Your leave-when-ready style means you'll never be watching a timetable.");
  return bits.join(" ");
}

function buildPlan(answers, sorted) {
  const plan = [];
  const [first, second] = sorted.map(([k]) => k);
  const journey = answers.journey;
  plan.push([first, `${TRANSPORT[first].name} as your main way around — ${TRANSPORT[first].tagline.toLowerCase()}`]);
  if (second) plan.push([second, `${TRANSPORT[second].name} for the trips it does best${second === "ferry" ? " — harbour and West End days" : second === "taxi" ? " — airport transfers and evenings" : ""}`]);
  if (journey === 0 || answers.timing === 1) {
    if (!["taxi", "rideshare"].includes(first) && !["taxi", "rideshare"].includes(second))
      plan.push(["taxi", "A taxi for airport transfers and any important timed journeys"]);
  }
  plan.push(["shorelink", "Shorelink for digital bus and ferry fares"]);
  return plan;
}

export default function TransportQuiz() {
  usePageMeta("How Should I Get Around Bermuda?", "Take The Bermuda Co-Host's quick transportation quiz to discover whether a taxi, Bermuda bus, ferry, regulated ride share or electric microcar best fits your Bermuda vacation.");
  const [phase, setPhase] = useState("intro");
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (saved && saved.answers) { setAnswers(saved.answers); setPhase(saved.phase || "intro"); setIdx(saved.idx || 0); }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, phase, idx }));
  }, [answers, phase, idx]);

  const question = QUIZ_QUESTIONS[idx];
  const selected = answers[question?.id];

  const results = useMemo(() => {
    const scores = { taxi: 0, rideshare: 0, bus: 0, ferry: 0, microcar: 0 };
    QUIZ_QUESTIONS.forEach((q) => {
      const a = answers[q.id];
      if (a === undefined) return;
      Object.entries(q.options[a].scores).forEach(([k, v]) => { scores[k] += v; });
    });
    return Object.entries(scores).sort((a, b) => b[1] - a[1]);
  }, [answers]);

  const start = () => { setPhase("quiz"); setIdx(0); track("transport_quiz_started"); };
  const choose = (optIdx) => setAnswers((a) => ({ ...a, [question.id]: optIdx }));
  const next = () => {
    if (selected === undefined) return;
    track("transport_quiz_question", { question: question.id, answer: question.options[selected].label });
    if (idx === QUIZ_QUESTIONS.length - 1) {
      setPhase("result");
      track("transport_quiz_completed", { winner: results[0][0] });
      track(`transport_${results[0][0]}_recommended`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else setIdx(idx + 1);
  };
  const back = () => idx > 0 && setIdx(idx - 1);
  const restart = () => {
    setAnswers({}); setIdx(0); setPhase("intro"); setShowCompare(false);
    localStorage.removeItem(STORAGE_KEY);
    track("transport_quiz_restarted");
  };

  return (
    <main>
      <section className="relative overflow-hidden bg-navy text-ivory">
        <div className="absolute inset-0">
          <img src={IMAGES.hamilton} alt="Hamilton harbour, Bermuda" className="h-full w-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/60 to-navy" />
        </div>
        <div className="noise-overlay relative z-10 mx-auto max-w-[900px] px-6 pb-20 pt-40 text-center lg:px-10">
          <Reveal><p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-bermuda">Guest Tools</p></Reveal>
          <MaskedLines lines={["How Should I Get", "Around Bermuda?"]} className="mx-auto mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight" />
          <Reveal delay={0.4}>
            <p className="mx-auto mt-6 max-w-xl text-base text-ivory/75">
              Not sure whether to take the bus, ferry, taxi, ride share or rent an electric microcar?
              Answer a few quick questions and we'll recommend the transportation options that best fit your Bermuda vacation.
            </p>
          </Reveal>
          {phase === "intro" && (
            <Reveal delay={0.55}>
              <div className="mt-9">
                <button onClick={start} data-testid="quiz-start-btn" className="bg-bermuda px-9 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-ivory focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bermuda">
                  Start the Quiz
                </button>
                <p className="mt-4 text-xs text-ivory/50">Takes less than 1 minute</p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {phase === "quiz" && (
        <section className="mx-auto max-w-[760px] px-6 py-16 lg:px-10" data-testid="quiz-panel">
          <div className="mb-8">
            <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-navy/50">
              <span data-testid="quiz-question-number">Question {idx + 1} of {QUIZ_QUESTIONS.length}</span>
              <button onClick={restart} data-testid="quiz-restart-btn" className="inline-flex items-center gap-1.5 hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C48B7F]">
                <RotateCcw size={11} /> Restart Quiz
              </button>
            </div>
            <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-navy/10" role="progressbar" aria-valuenow={idx + 1} aria-valuemin={1} aria-valuemax={QUIZ_QUESTIONS.length}>
              <motion.div className="h-full bg-bermuda-dark" animate={{ width: `${((idx + 1) / QUIZ_QUESTIONS.length) * 100}%` }} transition={{ duration: 0.5 }} />
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: 32 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -32 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <h2 className="font-serif text-2xl text-navy sm:text-3xl">{question.title}</h2>
              <div className="mt-7 grid gap-3" role="radiogroup" aria-label={question.title}>
                {question.options.map((opt, i) => (
                  <button
                    key={i}
                    role="radio"
                    aria-checked={selected === i}
                    onClick={() => choose(i)}
                    data-testid={`quiz-option-${i}`}
                    className={`flex items-start gap-4 border p-5 text-left transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C48B7F] ${
                      selected === i ? "border-navy bg-navy text-ivory shadow-lg" : "border-navy/15 bg-white hover:border-bermuda"
                    }`}
                  >
                    <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${selected === i ? "border-bermuda bg-bermuda text-navy" : "border-navy/25 text-transparent"}`}>
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span>
                      <span className="block text-[15px] font-medium">{opt.label}</span>
                      {opt.sub && <span className={`mt-1 block text-[13px] leading-relaxed ${selected === i ? "text-ivory/65" : "text-navy/55"}`}>{opt.sub}</span>}
                    </span>
                  </button>
                ))}
              </div>

              <div className="mt-8 flex items-center justify-between">
                <button
                  onClick={back}
                  disabled={idx === 0}
                  data-testid="quiz-back-btn"
                  className="inline-flex items-center gap-2 border border-navy/20 px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C48B7F]"
                >
                  <ArrowLeft size={13} /> Back
                </button>
                <button
                  onClick={next}
                  disabled={selected === undefined}
                  data-testid="quiz-next-btn"
                  className="inline-flex items-center gap-2 bg-navy px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-ivory transition-colors hover:bg-bermuda-dark disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bermuda"
                >
                  {idx === QUIZ_QUESTIONS.length - 1 ? "See My Result" : "Next"} <ArrowRight size={13} />
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </section>
      )}

      {phase === "result" && (
        <ResultView answers={answers} results={results} showCompare={showCompare} setShowCompare={setShowCompare} restart={restart} />
      )}
    </main>
  );
}

function ResultView({ answers, results, showCompare, setShowCompare, restart }) {
  const [winnerKey] = results[0];
  const winner = TRANSPORT[winnerKey];
  const WinnerIcon = ICONS[winner.icon];
  const secondary = results.slice(1, 3);
  const plan = buildPlan(answers, results);

  return (
    <div data-testid="quiz-result">
      <section className="mx-auto max-w-[900px] px-6 py-16 lg:px-10">
        <Reveal>
          <p className="text-center text-[11px] font-semibold uppercase tracking-[0.3em] text-bermuda-dark">Your Bermuda Transport Match</p>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="mt-8 border border-navy/10 bg-white p-10 text-center shadow-xl shadow-navy/5" data-testid="quiz-winner-card">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-pink-soft text-bermuda-dark">
              <WinnerIcon size={26} strokeWidth={1.5} />
            </span>
            <h2 className="mt-6 font-serif text-4xl font-light text-navy" data-testid="quiz-winner-name">{winner.name}</h2>
            <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-bermuda-dark">{winner.tagline}</p>
            <p className="mx-auto mt-6 max-w-xl text-[15px] leading-relaxed text-navy/70" data-testid="quiz-explanation">{buildExplanation(winnerKey, answers)}</p>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-navy/55">{winner.whyBase}</p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-6 border-l-2 border-bermuda bg-sand-light p-7" data-testid="quiz-good-to-know">
            <h3 className="flex items-center gap-2.5 font-serif text-xl text-navy"><Info size={16} className="text-bermuda-dark" /> Good to Know</h3>
            <ul className="mt-4 space-y-2.5">
              {winner.goodToKnow.map((f) => (
                <li key={f} className="flex items-start gap-3 text-[13px] text-navy/75">
                  <Check size={14} strokeWidth={2.5} className="mt-0.5 shrink-0 text-ocean" /> {f}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-12">
            <h3 className="font-serif text-2xl text-navy">Also Consider</h3>
            <p className="mt-2 text-sm text-navy/55">Most Bermuda visitors mix two or three options during one trip.</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {secondary.map(([key]) => {
                const t = TRANSPORT[key];
                const Icon = ICONS[t.icon];
                return (
                  <div key={key} className="flex items-start gap-4 border border-navy/10 bg-white p-6" data-testid={`quiz-secondary-${key}`}>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ocean-light text-ocean"><Icon size={17} strokeWidth={1.5} /></span>
                    <div>
                      <h4 className="font-serif text-lg text-navy">{t.name}</h4>
                      <p className="mt-1 text-[13px] text-navy/60">{t.tagline}.</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-12 bg-navy p-8 text-ivory lg:p-10" data-testid="quiz-transport-plan">
            <h3 className="font-serif text-2xl">Your Bermuda Transport Strategy</h3>
            <ul className="mt-6 space-y-4">
              {plan.map(([key, text], i) => {
                const Icon = key === "shorelink" ? Smartphone : ICONS[TRANSPORT[key].icon];
                return (
                  <li key={i} className="flex items-start gap-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-bermuda/40 text-bermuda"><Icon size={15} strokeWidth={1.5} /></span>
                    <p className="pt-2 text-sm leading-relaxed text-ivory/80">{text}</p>
                  </li>
                );
              })}
            </ul>
          </div>
        </Reveal>

        <div className="mt-10 text-center">
          <button
            onClick={() => { setShowCompare(!showCompare); track("transport_compare_toggled"); }}
            data-testid="quiz-compare-btn"
            className="border border-navy/25 px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C48B7F]"
          >
            {showCompare ? "Hide Comparison" : "Compare All Transport Options"}
          </button>
        </div>

        <AnimatePresence>
          {showCompare && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
              data-testid="quiz-compare-section"
            >
              <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(TRANSPORT).map(([key, t]) => {
                  const Icon = ICONS[t.icon];
                  return (
                    <div key={key} className="border border-navy/10 bg-white p-6">
                      <h4 className="flex items-center gap-2.5 font-serif text-lg text-navy"><Icon size={16} className="text-bermuda-dark" /> {t.name}</h4>
                      <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-navy/45">Best for</p>
                      <ul className="mt-2 space-y-1.5">
                        {t.bestFor.map((b) => <li key={b} className="text-[13px] text-navy/70">· {b}</li>)}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <Reveal delay={0.1}>
          <div className="mt-14 flex flex-col items-center gap-4 border border-ocean/25 bg-ocean-light p-8 text-center sm:flex-row sm:text-left" data-testid="shorelink-section">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-ocean text-white"><Smartphone size={22} strokeWidth={1.5} /></span>
            <div className="flex-1">
              <h3 className="font-serif text-2xl text-navy">Meet Shorelink</h3>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-ocean">Bermuda's digital bus & ferry companion</p>
              <p className="mt-3 text-sm leading-relaxed text-navy/65">{SHORELINK.blurb}</p>
            </div>
            <div className="flex shrink-0 flex-col gap-2.5">
              <a href={SHORELINK.url} target="_blank" rel="noopener noreferrer" data-testid="shorelink-learn-btn" onClick={() => track("shorelink_clicked", { type: "learn" })} className="inline-flex items-center gap-2 bg-navy px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-ivory transition-colors hover:bg-bermuda-dark">
                Learn About Shorelink <ExternalLink size={12} />
              </a>
              <a href={SHORELINK.url} target="_blank" rel="noopener noreferrer" data-testid="shorelink-get-btn" onClick={() => track("shorelink_clicked", { type: "get" })} className="inline-flex items-center gap-2 border border-navy/25 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory">
                Get Shorelink <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </Reveal>

        <div className="mt-8 text-center">
          <button onClick={restart} data-testid="quiz-retake-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-navy/45 hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C48B7F]">
            <RotateCcw size={12} /> Restart Quiz
          </button>
        </div>
      </section>

      <section className="bg-navy text-ivory">
        <div className="noise-overlay mx-auto max-w-[1100px] px-6 py-20 text-center lg:px-10">
          <Reveal><Anchor size={22} className="mx-auto text-bermuda" strokeWidth={1.2} /></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-serif text-3xl font-light sm:text-4xl">Staying with The Bermuda Co-Host?</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-ivory/70">
              We'll help make getting around Bermuda easier. Your host can help you understand airport transportation,
              taxi arrangements, public transportation, electric vehicle rentals, ferry options and transportation near your vacation rental.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <Link
              to="/guest-support"
              data-testid="ask-cohost-btn"
              onClick={() => track("ask_cohost_clicked")}
              className="mt-9 inline-block bg-bermuda px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-ivory"
            >
              Ask Your Bermuda Co-Host
            </Link>
          </Reveal>
          <p className="mx-auto mt-12 max-w-xl text-[11px] leading-relaxed text-ivory/40">
            Transportation routes, schedules, fares, app availability, rental requirements and service availability may change.
            Always confirm current information with the relevant Bermuda transportation provider before travelling.
          </p>
        </div>
      </section>
    </div>
  );
}
